import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Download } from 'lucide-react';
import { useMobileDetection } from '../../hooks/useMobileDetection';
import { MobileRestrictionMessage } from '@/components/ui/MobileRestrictionMessage';

function RepPopup({ text, closePopup, ActiveRepList, InactiveRepList }) {
    const [activeReps, setActiveReps] = useState(ActiveRepList || []);
    const [inactiveReps, setInactiveReps] = useState(InactiveRepList || []);
    const [loading, setLoading] = useState(false);
    const isMobile = useMobileDetection();
    
    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });

    useEffect(() => {
        if (!ActiveRepList && !InactiveRepList && userdata?.wholesaler) {
            fetchReps();
        }
    }, [userdata]);

    const fetchReps = async () => {
        setLoading(true);
        try {
            const userName = userdata.paramsname;
            
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    roleName: userName,
                    roleType: userdata.wholesaler,
                    wholesaler_Id: userdata.wholesaler_Id,
                    distributor_UserId: userdata.distributor_UserId
                })
            };
            
            const response = await fetch(`/api/ds/repsNew`, requestOptions);
            const data = await response.json();
            
            if (data?.length > 0) {
                const userEmail = userdata.email;
                // Find the item matching the logged-in user's email
                const userItem = data.find(item => 
                    item.emailId?.toLowerCase() === userEmail?.toLowerCase()
                );
                
                if (userItem) {
                    setActiveReps(JSON.parse(userItem.activeJson || '[]'));
                    setInactiveReps(JSON.parse(userItem.inActiveJson || '[]'));
                }
            }
        } catch (err) {
            console.error('Error fetching reps:', err);
        } finally {
            setLoading(false);
        }
    };

    const exportReps = useCallback((type) => {
        try {
            const reps = type === 'active' ? activeReps : inactiveReps;
            if (!reps.length) return;

            // Prepare CSV data
            const headers = ['Name', 'E-mail', 'Assigned Group', 'Commission'];
            const csvRows = [headers.join(',')];

            reps.forEach(rep => {
                const name = rep.SubDisName || '';
                const email = rep.SubDisEmail || '';
                
                // If rep has multiple groups, create a row for each group
                if (rep.GroupList && rep.GroupList.length > 0) {
                    rep.GroupList.forEach(group => {
                        const groupName = group.Group.split(' ')[0] || '';
                        const commission = group.Group.split('(')[1]?.replace(')', '') || '';
                        
                        // Escape fields that contain commas
                        const escapedName = name.includes(',') ? `"${name}"` : name;
                        const escapedEmail = email.includes(',') ? `"${email}"` : email;
                        
                        csvRows.push([escapedName, escapedEmail, groupName, commission].join(','));
                    });
                } else {
                    const escapedName = name.includes(',') ? `"${name}"` : name;
                    const escapedEmail = email.includes(',') ? `"${email}"` : email;
                    csvRows.push([escapedName, escapedEmail, '', ''].join(','));
                }
            });

            const csvData = csvRows.join('\n');

            // Create and download CSV file
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${type}_reps.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting reps:', error);
        }
    }, [activeReps, inactiveReps]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup();
        }
    }, [closePopup]);

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>
                
                <div className="flex flex-col gap-4">
                    {loading ? (
                        <div className="flex items-center justify-center p-8 min-h-[120px] flex flex-col items-center justify-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                            <p className="text-sm text-muted-foreground">Please wait...</p>
                        </div>
                    ) : isMobile ? (
                        <MobileRestrictionMessage />
                    ) : (activeReps.length === 0 && inactiveReps.length === 0) ? (
                        <div className="flex items-center justify-center p-8 text-muted-foreground text-center text-[15px] py-8 text-center">
                            <p className="text-muted-foreground">No Reps Found</p>
                        </div>
                    ) : (
                        <Tabs defaultValue="active" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="active">Active Reps ({activeReps.length})</TabsTrigger>
                                <TabsTrigger value="inactive">Inactive Reps ({inactiveReps.length})</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="active" className="mt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-sm text-muted-foreground">
                                        Reps with greater than 1 claim in last 30 days
                                    </p>
                                    <Button variant="secondary" size="sm" onClick={() => exportReps('active')}>
                                        <Download className="h-4 w-4 mr-2" />
                                        Export
                                    </Button>
                                </div>
                                <div className="rounded-md border overflow-hidden">
                                    <div className="max-h-[300px] overflow-auto">
                                        <table className="w-full text-sm">
                                            <thead className="sticky top-0 z-10 bg-card border-b">
                                                <tr>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">E-mail</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Assigned Group</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Commission</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                                {activeReps.length > 0 ? (
                                                    activeReps.map((rep, index) => (
                                                        <tr key={index} className="hover:bg-accent">
                                                            <td className="px-4 py-3 font-medium text-foreground break-words">{rep.SubDisName || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-muted-foreground break-words">{rep.SubDisEmail || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-muted-foreground">
                                                                {rep.GroupList?.map((group, gIndex) => (
                                                                    <div key={gIndex}>{group.Group.split(' ')[0]}</div>
                                                                ))}
                                                            </td>
                                                            <td className="px-4 py-3 text-muted-foreground">
                                                                {rep.GroupList?.map((group, gIndex) => (
                                                                    <div key={gIndex}>{group.Group.split('(')[1]?.replace(')', '') || ''}</div>
                                                                ))}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Reps not available</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </TabsContent>
                            
                            <TabsContent value="inactive" className="mt-4">
                                <div className="flex justify-between items-center mb-4">
                                    <p className="text-sm text-muted-foreground">
                                        Reps with less than 1 claim in last 30 days
                                    </p>
                                    <Button variant="secondary" size="sm" onClick={() => exportReps('inactive')}>
                                        <Download className="h-4 w-4 mr-2" />
                                        Export
                                    </Button>
                                </div>
                                <div className="rounded-md border overflow-hidden">
                                    <div className="max-h-[300px] overflow-auto">
                                        <table className="w-full text-sm">
                                            <thead className="sticky top-0 z-10 bg-card border-b">
                                                <tr>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Name</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">E-mail</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Assigned Group</th>
                                                    <th className="text-left px-4 py-3 font-semibold text-foreground">Commission</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border">
                                                {inactiveReps.length > 0 ? (
                                                    inactiveReps.map((rep, index) => (
                                                        <tr key={index} className="hover:bg-accent">
                                                            <td className="px-4 py-3 font-medium text-foreground break-words">{rep.SubDisName || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-muted-foreground break-words">{rep.SubDisEmail || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-muted-foreground">
                                                                {rep.GroupList?.map((group, gIndex) => (
                                                                    <div key={gIndex}>{group.Group.split(' ')[0]}</div>
                                                                ))}
                                                            </td>
                                                            <td className="px-4 py-3 text-muted-foreground">
                                                                {rep.GroupList?.map((group, gIndex) => (
                                                                    <div key={gIndex}>{group.Group.split('(')[1]?.replace(')', '') || ''}</div>
                                                                ))}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Reps not available</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default RepPopup;
